/**
 * L'onglet Contrôles de la fiche d'un élève : `(état, élève) → props` (patron A).
 *
 * `SchoolAssessmentsScreen.js` en contexte tuteur (`isTutorContext` : un `studentId` en paramètre, qui
 * n'est pas le compte), sur les adaptateurs de « Mes contrôles » de l'élève :
 * - la liste = `schoolAssessments.byStudentId[élève]`, rangée par le réducteur à la réponse de
 *   `getSchoolAssessments { student_id, upcoming: true }` et tenue à jour à chaque ajout, modification
 *   ou suppression ;
 * - la feuille d'ajout lit l'élève que le tuteur regarde (`AddSchoolAssessmentModal`, `user={targetStudent}`) :
 *   sa classe (les chapitres du catalogue) et ses objectifs (les matières proposées). L'élève vient de la
 *   liste du tuteur (`selectDisplayedStudent(état, id)`), qui porte ce que la route de l'ancien écran
 *   recevait (`student`, celui de `NextAssessmentWidget`) ;
 * - la phrase vide nomme l'élève par son prénom : « Ajoute un contrôle pour {prénom} », sinon « l'élève ».
 *
 * Une fabrique : la fiche peut être montée deux fois, chacune avec son élève.
 */
import { createSelector } from "reselect"

import {
    projectStudentAssessments,
    type StudentAssessmentsViewModel,
} from "../../student/Assessments/studentAssessmentsViewModel"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any
type StudentId = string | number | null | undefined

export type TutorStudentAssessmentsViewModel = StudentAssessmentsViewModel & {
    /** Le prénom de l'élève, tel que la liste du tuteur le porte. */
    firstname: string | null
    /** « Ajoute un contrôle pour {prénom} » (`emptyStateMessage` de l'ancien écran). */
    emptyMessage: string
}

/** Le prénom de l'élève, sinon « l'élève » (`targetStudent?.firstname || "l'élève"`). */
export function tutorAssessmentsEmptyMessage(firstname: string | null | undefined): string {
    return `Ajoute un contrôle pour ${firstname || "l'élève"}`
}

const studentOf = (state: ReduxState, studentId: StudentId) =>
    studentId != null ? selectDisplayedStudent(state, studentId) : null

export function makeTutorStudentAssessmentsViewModel() {
    return createSelector(
        [
            (_state: ReduxState, studentId: StudentId) => studentId ?? null,
            (state: ReduxState, studentId: StudentId) =>
                studentOf(state, studentId)?.profile?.class as string | undefined,
            (state: ReduxState, studentId: StudentId) => studentOf(state, studentId)?.firstname as string | undefined,
            (state: ReduxState, studentId: StudentId) => studentOf(state, studentId)?.pedagogical_objectives,
            (state: ReduxState) => state?.data?.schoolAssessments?.byStudentId,
            (state: ReduxState) => state?.data?.staticData?.school_subjects,
            (state: ReduxState) => state?.api?.getSchoolAssessments,
        ],
        (
            studentId,
            studentClass,
            firstname,
            objectives,
            byStudentId,
            subjects,
            req
        ): TutorStudentAssessmentsViewModel => ({
            ...projectStudentAssessments({ studentId, studentClass, objectives, byStudentId, subjects, req }),
            firstname: firstname || null,
            emptyMessage: tutorAssessmentsEmptyMessage(firstname),
        })
    )
}
